import {
  Button,
  ButtonGroup,
  Form,
  Tab,
  Tabs,
  Overlay,
  Badge,
} from 'react-bootstrap';
import Card from '../../../../shared/Card';
import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  deleteItemAndRelationships,
  deleteItemRelationships,
  getItems,
  getRelationships,
  upsertRelationship,
  upsertItem,
  deleteRelationship,
} from './api/items-and-relationships-api';
import HierarchyDisplay from './hierarchy-display/HierarchyDisplay';
import TextHierarchyDisplay from './hierarchy-display/TextHierarchyDisplay';
import CompareItemsForm from './CompareForm';
import AppContext from '../../../../AppContext';
import {
  Id,
  ItemType,
  ItemsRecord,
  ListsRecord,
  RelationshipsRecord,
} from './types';
import { deleteList } from './api/lists-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import SelectedItemPanel from './SelectedItemPanel';
import DeleteButton from './DeleteButton';
import RelationshipsList from './Relationships';
import ItemsList from './Items';

export const DEFAULT_LIST_ID = 'my values';

const NEW_ITEM: ItemType = Object.freeze({ id: '' });

interface AddItemFormProps {
  onSubmit: (params: { item: ItemType; onSuccess: () => void }) => void;
}

const AddItemForm = ({ onSubmit }: AddItemFormProps) => {
  const [newItem, setNewItem] = useState<ItemType>(NEW_ITEM);
  const clearNewItem = () => setNewItem(NEW_ITEM);
  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ item: newItem, onSuccess: clearNewItem });
      }}
    >
      <ButtonGroup style={{ width: '100%' }}>
        <Form.Control
          type="text"
          placeholder="item name"
          value={newItem.id}
          onChange={(e) => {
            setNewItem({ ...newItem, id: e.target.value });
          }}
          autoFocus={true}
        />
        <Button
          type="submit"
          variant="success"
          style={{ whiteSpace: 'nowrap' }}
        >
          Add Item
        </Button>
      </ButtonGroup>
    </Form>
  );
};

interface DeleteListButtonProps {
  onClick: () => void;
}

const DeleteListButton = ({ onClick }: DeleteListButtonProps) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button variant="light" size="sm" onClick={() => setShow(!show)}>
        ...
      </Button>
      {show && (
        <Button
          variant="danger"
          size="sm"
          className="float-end"
          onClick={() => {
            onClick();
            setShow(false);
          }}
        >
          DELETE LIST
        </Button>
      )}
    </>
  );
};

export default function Editor({
  listId,
  lists,
  loadLists,
  selectListId,
}: {
  listId: Id;
  lists: ListsRecord;
  loadLists: () => void;
  selectListId: (listId: Id) => void;
}) {
  const { user } = useContext(AppContext);

  const [items, setItems] = useState<ItemsRecord>({});
  const [relationships, setRelationships] = useState<RelationshipsRecord>({});
  const [itemsToCompare, setItemsToCompare] = useState<ItemType[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<Id | null>(null);
  const [showCompareTooltip, setShowCompareTooltip] = useState(false);
  const [showHierarchyTooltip, setShowHierarchyTooltip] = useState(false);
  const compareRef = useRef(null);
  const hierarchyRef = useRef(null);

  const list = useMemo(
    () => lists[listId] || { id: listId, name: '' },
    [listId, lists]
  );

  const loadItems = async () => {
    if (!listId) return;
    const items = await getItems(user.uid, listId);
    setItems(items);
  };

  const loadRelationships = async () => {
    if (!listId) return;
    const relationships = await getRelationships(user.uid, listId);
    setRelationships(relationships);
  };

  useEffect(() => {
    if (!user) return;
    loadLists();
    void loadItems();
    void loadRelationships();
  }, [listId]);

  const handleDeleteItem = async (itemId: Id) => {
    await deleteItemAndRelationships(user.uid, listId, itemId, () => {});
    await loadItems();
    await loadRelationships();
  };

  const handleDeleteRelationships = async (itemId: Id) => {
    await deleteItemRelationships(user.uid, listId, itemId);
    await loadRelationships();
  };

  return (
    <Card>
      <h1 className="mt-3">
        {list.name}
        <span className="ms-2">
          <DeleteListButton
            onClick={() => {
              deleteList(user.uid, listId, () => {
                loadLists();
                selectListId(DEFAULT_LIST_ID);
              });
            }}
          />
        </span>
      </h1>
      <Tabs defaultActiveKey="main" className="my-3">
        <Tab eventKey="main" title="Main">
          <div className="row">
            <div className="col-md-8">
              <div className="my-4">
                <h5 className="text-muted mb-3">
                  Hierarchy{' '}
                  <span
                    ref={hierarchyRef}
                    onMouseEnter={() => setShowHierarchyTooltip(true)}
                    onMouseLeave={() => setShowHierarchyTooltip(false)}
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-muted"
                      style={{ fontSize: '0.8em', cursor: 'help' }}
                    />
                  </span>
                  <Overlay
                    target={hierarchyRef.current}
                    show={showHierarchyTooltip}
                    placement="right"
                  >
                    {(props) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          backgroundColor: '#f8f9fa',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem',
                          maxWidth: '300px',
                        }}
                      >
                        Your items organized by their relationships.
                      </div>
                    )}
                  </Overlay>
                </h5>
                <Card>
                  <HierarchyDisplay
                    items={items}
                    relationships={relationships}
                    addItemForCompare={(id) => {
                      if (
                        items[id] &&
                        !itemsToCompare.some((item) => item?.id === id)
                      ) {
                        setItemsToCompare(
                          [items[id], ...itemsToCompare].slice(0, 2)
                        );
                      }
                    }}
                    deleteItem={handleDeleteItem}
                    deleteRelationships={handleDeleteRelationships}
                    onItemClick={setSelectedItemId}
                  />
                </Card>
              </div>
              <div className="my-4">
                <h5 className="text-muted mb-3">Add Item</h5>
                <Card>
                  <AddItemForm
                    onSubmit={async ({ item, onSuccess }) => {
                      await upsertItem(user.uid, listId, item);
                      await loadItems();
                      onSuccess();
                    }}
                  />
                </Card>
              </div>
            </div>
            <div className="col-md-4">
              <div className="my-4">
                <h5 className="text-muted mb-3">
                  Compare{' '}
                  <span
                    ref={compareRef}
                    onMouseEnter={() => setShowCompareTooltip(true)}
                    onMouseLeave={() => setShowCompareTooltip(false)}
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-muted"
                      style={{ fontSize: '0.8em', cursor: 'help' }}
                    />
                  </span>
                  <Overlay
                    target={compareRef.current}
                    show={showCompareTooltip}
                    placement="right"
                  >
                    {(props) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          backgroundColor: '#f8f9fa',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem',
                          fontSize: '0.875rem',
                          maxWidth: '300px',
                        }}
                      >
                        Choose which item is more important to you. This creates
                        relationships that organize your items into a hierarchy.
                      </div>
                    )}
                  </Overlay>
                </h5>
                <Card>
                  {items && Object.keys(items).length >= 2 && (
                    <CompareItemsForm
                      items={items}
                      itemsToCompare={itemsToCompare}
                      relationships={relationships}
                      addRelationship={async (
                        relationship,
                        onSuccess = () => null
                      ) => {
                        await upsertRelationship(
                          user.uid,
                          listId,
                          relationship
                        );
                        await loadRelationships();
                        onSuccess();
                      }}
                      onItemSelect={setSelectedItemId}
                    />
                  )}
                </Card>
              </div>

              {selectedItemId && items[selectedItemId] ? (
                <SelectedItemPanel
                  item={items[selectedItemId]}
                  relationships={relationships}
                  onDelete={handleDeleteItem}
                  onDeleteRelationships={handleDeleteRelationships}
                />
              ) : (
                <Card title="Selected Item">
                  <div className="p-3 text-muted">
                    Select an item to view its details
                  </div>
                </Card>
              )}
            </div>
          </div>
        </Tab>
        <Tab eventKey="hierarchy" title="Hierarchy">
          <div className="mb-4">
            <Card>
              <HierarchyDisplay
                deleteItem={handleDeleteItem}
                deleteRelationships={handleDeleteRelationships}
                items={items}
                relationships={relationships}
                addItemForCompare={(id) => {
                  if (!itemsToCompare.some((item) => item.id === id)) {
                    setItemsToCompare(
                      [items[id], ...itemsToCompare].slice(0, 2)
                    );
                  }
                }}
              />
            </Card>
          </div>
          <div>
            <h5 className="text-muted mb-3">Text View</h5>
            <Card>
              <TextHierarchyDisplay
                items={items}
                relationships={relationships}
              />
            </Card>
          </div>
        </Tab>
        <Tab eventKey="compare" title="Compare">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div>
              <CompareItemsForm
                items={items}
                itemsToCompare={itemsToCompare}
                relationships={relationships}
                addRelationship={async (
                  relationship,
                  onSuccess = () => null
                ) => {
                  await upsertRelationship(user.uid, listId, relationship);
                  await loadRelationships();
                  onSuccess();
                }}
              />
            </div>
            <div>
              <HierarchyDisplay
                deleteItem={handleDeleteItem}
                deleteRelationships={handleDeleteRelationships}
                items={items}
                relationships={relationships}
                addItemForCompare={(id) => {
                  if (!itemsToCompare.some((item) => item.id === id)) {
                    setItemsToCompare(
                      [items[id], ...itemsToCompare].slice(0, 2)
                    );
                  }
                }}
              />
            </div>
          </div>
        </Tab>
        <Tab
          eventKey="items"
          title={
            <>
              Items
              {items && Object.keys(items).length > 0 && (
                <Badge className="ms-2">{Object.keys(items).length}</Badge>
              )}
            </>
          }
        >
          <Card title="Items">
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
            >
              <AddItemForm
                onSubmit={async ({ item, onSuccess = () => null }) => {
                  await upsertItem(user.uid, listId, item);
                  await loadItems();
                  onSuccess();
                }}
              />

              <div>
                {!items || Object.keys(items).length < 1 ? (
                  <pre className="text-info">Add some items</pre>
                ) : (
                  <ItemsList
                    items={items}
                    onDeleteItem={handleDeleteItem}
                    onDeleteRelationships={handleDeleteRelationships}
                  />
                )}
              </div>
            </div>
          </Card>
        </Tab>
        <Tab
          eventKey="relationships"
          title={
            <>
              Relationships
              {relationships && Object.keys(relationships).length > 0 && (
                <Badge className="ms-2">
                  {Object.keys(relationships).length}
                </Badge>
              )}
            </>
          }
        >
          <Card className="mt-3" title="Relationships">
            {!items || Object.keys(items).length < 2 ? (
              <pre className="text-info">Requires 2 or more items</pre>
            ) : (
              <div className="p-3">
                <RelationshipsList
                  relationships={relationships}
                  deleteRelationship={async ({ id }) => {
                    await deleteRelationship(user.uid, listId, id);
                    await loadRelationships();
                  }}
                />
              </div>
            )}
          </Card>
        </Tab>
      </Tabs>
    </Card>
  );
}
